import React, { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useEMTDispatch } from '@/store'
import { useFirstRouterQueryParam } from '@/hooks'
import { AuthServiceHelper } from '@/modules/Auth'
import { _saveItem, TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { refreshTokenAsync } from '@/modules/Auth/BasicAuthServiceHelper'

export type withPublicRouteProps = unknown

const withPublicRoute =
  () =>
  <Config,>(WrappedComponent: React.FC<Config & withPublicRouteProps>) => {
    const PublicRoute: React.FC<Config> = (props) => {
      const dispatch = useEMTDispatch()
      const router = useRouter()

      // URL params (SSO + tokens + optional type)
      const [token, refreshToken, email, ssoToken] = useFirstRouterQueryParam(['token', 'refreshToken', 'email', 'sso'])

      const isFirstLaunch = useRef(true)
      const hasAttemptedSSO = useRef(false)
      const hasCleanedTokens = useRef(false)

      useEffect(() => {
        const bootstrapAuth = async () => {
          try {
            const refreshUser = isFirstLaunch.current

            await AuthServiceHelper.dispatchCheckAuthStatusAsync(dispatch)(refreshUser)
            isFirstLaunch.current = false

            // -------------------------
            // Save tokens + clean URL
            // -------------------------
            if ((token || refreshToken) && !hasCleanedTokens.current) {
              hasCleanedTokens.current = true

              if (token) {
                _saveItem(TokenKeys.Auth, token)
              }

              if (refreshToken) {
                _saveItem(TokenKeys.Refresh, refreshToken)
                refreshTokenAsync(refreshToken)
              }

              // 🔥 Clean URL
              const cleanedQuery = { ...router.query }
              delete cleanedQuery.token
              delete cleanedQuery.refreshToken

              router.replace(
                {
                  pathname: router.pathname,
                  query: cleanedQuery,
                },
                undefined,
                { shallow: true },
              )

              return
            }

            // -------------------------
            // SSO login
            // -------------------------
            if (email && ssoToken && !hasAttemptedSSO.current) {
              hasAttemptedSSO.current = true

              try {
                await AuthServiceHelper.dispatchLoginAsync(dispatch)({
                  email: email.replace(' ', '+'),
                  sso: ssoToken,
                })
              } catch (error) {
                console.debug('SSO login failed:', error)
              }
            }
          } catch (error) {
            isFirstLaunch.current = false
            console.debug('Public auth bootstrap error:', error)
          }
        }

        bootstrapAuth()
      }, [token, refreshToken, email, ssoToken, dispatch, router])

      // @ts-expect-error: needs to extend IntrinsicAttributes
      return <WrappedComponent {...props} />
    }

    return PublicRoute
  }

export default withPublicRoute
